import { useState, useEffect, useRef, useContext } from "react";
import useTemplates from "../../../services/Whatsapp/useTemplates";
import useWhatsappNumbers from "../../../services/Whatsapp/useWhatsappNumbers";
import "../../whatsapp/WhatsApp.css";
import { GlobalContext } from "../../../Context/GlobalContext";
import useMedia from "../../../services/Whatsapp/useMedia";
import NoIndex from "../../../components/NoIndex";
import { toast, ToastContainer } from "react-toastify";
import {
  emojiCategories,
  countryCodes,
} from "../Template/TemplateEmojies-codes";
import TemplateLeftForm from "./TemplateLeftForm";
import TemplateRightForm from "./TemplateRightForm";

const CreateTemplate = () => {
  const { getLanguages } = useTemplates();
  const { whatsappNumbers } = useWhatsappNumbers();

  const {
    createTemplate,
    createImageTemplate,
    createVideoTemplate,
    createDocumentTemplate,
    fetchVendorTemplates,
  } = useTemplates();

  const { uploadImage, uploadVideo, uploadDocument } = useMedia();

  const { secret, userID } = useContext(GlobalContext);
  const [languages, setLanguages] = useState([]);
  const [selectedNumberIndex, setSelectedNumberIndex] = useState("");
  const [includeHeader, setIncludeHeader] = useState(false);
  const [includeButtons, setIncludeButtons] = useState(false);
  const [headerImage, setHeaderImage] = useState(null);
  const [headerVideo, setHeaderVideo] = useState(null);
  const [headerDocument, setHeaderDocument] = useState(null);
  const textareaRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedEmojiCategory, setSelectedEmojiCategory] = useState("😀");
  const [selectedCode, setSelectedCode] = useState("91");
  const [variables, setVariables] = useState([]);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };

      reader.onerror = (error) => reject(error);
    });
  };

  const formatFileSize = (bytes = 0) => {
    if (!bytes) return "0 KB";

    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const getDocumentExtension = (fileName = "") => {
    const parts = fileName.split(".");
    return parts.length > 1 ? parts.pop().toUpperCase() : "DOC";
  };

  const getBodyVariableExamples = () => {
    const bodyText = template.body || "";

    // Supports {{1}}, {{ 1 }}, {{2}}, {{ 2 }}
    const matches = bodyText.match(/{{\s*\d+\s*}}/g) || [];

    if (matches.length === 0) {
      return [];
    }

    const variableNumbers = [
      ...new Set(
        matches
          .map((item) => item.match(/\d+/)?.[0])
          .filter(Boolean)
          .map(Number),
      ),
    ].sort((a, b) => a - b);

    return variableNumbers.map((num, index) => {
      return variables[num - 1]?.value?.trim() || `sample ${index + 1}`;
    });
  };

  const normalizeBodyVariablesForPayload = () => {
    const bodyText = template.body || "";

    const matches = bodyText.match(/{{\s*\d+\s*}}/g) || [];

    if (matches.length === 0) {
      return {
        text: bodyText,
        exampleValues: [],
      };
    }

    let counter = 0;
    const usedMap = {};

    const normalizedText = bodyText.replace(
      /{{\s*(\d+)\s*}}/g,
      (match, num) => {
        if (!usedMap[num]) {
          counter += 1;
          usedMap[num] = counter;
        }

        return `{{${usedMap[num]}}}`;
      },
    );

    const exampleValues = Object.keys(usedMap)
      .sort((a, b) => usedMap[a] - usedMap[b])
      .map((oldNum, index) => {
        return (
          variables[Number(oldNum) - 1]?.value?.trim() || `sample ${index + 1}`
        );
      });

    return {
      text: normalizedText,
      exampleValues,
    };
  };

  const buildMediaPayload = (mediaUrl, type) => {
    const components = [];

    // BODY
    if (template.body?.trim()) {
      const normalizedBody = normalizeBodyVariablesForPayload();

      const bodyComponent = {
        type: "BODY",
        text: normalizedBody.text,
      };

      if (normalizedBody.exampleValues.length > 0) {
        bodyComponent.example = {
          body_text: [normalizedBody.exampleValues],
        };
      }

      components.push(bodyComponent);
    }

    // FOOTER
    if (template.footer?.trim()) {
      components.push({
        type: "FOOTER",
        text: template.footer,
      });
    }

    // BUTTONS
    const buttons = [];

    if (includeButtons && template.showButton4_quick1?.trim()) {
      buttons.push({
        type: "QUICK_REPLY",
        text: template.showButton4_quick1,
      });
    }

    if (includeButtons && template.showButton4_quick2?.trim()) {
      buttons.push({
        type: "QUICK_REPLY",
        text: template.showButton4_quick2,
      });
    }

    if (
      includeButtons &&
      template.showButton4_quick3?.trim() &&
      template.showButton4_quick3_url?.trim()
    ) {
      buttons.push({
        type: "URL",
        text: template.showButton4_quick3,
        url: template.showButton4_quick3_url,
      });
    }

    if (
      includeButtons &&
      template.showButton4_quick4?.trim() &&
      template.showButton4_quick3_call?.trim()
    ) {
      buttons.push({
        type: "PHONE_NUMBER",
        text: template.showButton4_quick4,
        phone_number: template.showButton4_quick3_call,
      });
    }

    if (buttons.length > 0) {
      components.push({
        type: "BUTTONS",
        buttons,
      });
    }

    return {
      userid: userID,
      secret: secret || "string",
      name: template.name,
      language: template.language,
      category: template.category,
      purpose: "",
      phoneNumberId: selectedPhoneNumberId,
      [type]: mediaUrl,
      components,
    };
  };

  const buildCreateTemplatePayload = () => {
    const components = [];

    // BODY
    if (template.body?.trim()) {
      const bodyComponent = {
        type: "BODY",
        text: template.body,
      };

      const variableValues = getBodyVariableExamples();

      if (variableValues.length > 0) {
        bodyComponent.example = {
          body_text: [variableValues],
        };
      }

      components.push(bodyComponent);
    }

    // HEADER TEXT
    if (
      includeHeader &&
      template.headerType === "Text" &&
      template.headerText?.trim()
    ) {
      components.push({
        type: "HEADER",
        format: "TEXT",
        text: template.headerText,
      });
    }

    // FOOTER
    if (template.footer?.trim()) {
      components.push({
        type: "FOOTER",
        text: template.footer,
      });
    }

    // BUTTONS
    const buttons = [];

    if (includeButtons && template.showButton4_quick1?.trim()) {
      buttons.push({
        type: "QUICK_REPLY",
        text: template.showButton4_quick1,
      });
    }

    if (includeButtons && template.showButton4_quick2?.trim()) {
      buttons.push({
        type: "QUICK_REPLY",
        text: template.showButton4_quick2,
      });
    }

    if (
      includeButtons &&
      template.showButton4_quick3?.trim() &&
      template.showButton4_quick3_url?.trim()
    ) {
      buttons.push({
        type: "URL",
        text: template.showButton4_quick3,
        url: template.showButton4_quick3_url,
      });
    }

    if (
      includeButtons &&
      template.showButton4_quick4?.trim() &&
      template.showButton4_quick3_call?.trim()
    ) {
      buttons.push({
        type: "PHONE_NUMBER",
        text: template.showButton4_quick4,
        phone_number: template.showButton4_quick3_call,
      });
    }

    if (buttons.length > 0) {
      components.push({
        type: "BUTTONS",
        buttons,
      });
    }

    return {
      userid: userID,
      secret: secret || "string",
      name: template.name,
      language: template.language,
      category: template.category,
      purpose: "",
      phoneNumberId: selectedPhoneNumberId,
      components,
    };
  };

  const buildDocumentTemplatePayload = (documentUrl) => {
    const components = [];

    // BODY
    if (template.body?.trim()) {
      const normalizedBody = normalizeBodyVariablesForPayload();

      const bodyComponent = {
        type: "BODY",
        text: normalizedBody.text,
      };

      if (normalizedBody.exampleValues.length > 0) {
        bodyComponent.example = {
          body_text: [normalizedBody.exampleValues],
        };
      }

      components.push(bodyComponent);
    }

    // FOOTER
    if (template.footer?.trim()) {
      components.push({
        type: "FOOTER",
        text: template.footer,
      });
    }

    // BUTTONS
    const buttons = [];

    if (includeButtons && template.showButton4_quick1?.trim()) {
      buttons.push({
        type: "QUICK_REPLY",
        text: template.showButton4_quick1,
      });
    }

    if (includeButtons && template.showButton4_quick2?.trim()) {
      buttons.push({
        type: "QUICK_REPLY",
        text: template.showButton4_quick2,
      });
    }

    if (
      includeButtons &&
      template.showButton4_quick3?.trim() &&
      template.showButton4_quick3_url?.trim()
    ) {
      buttons.push({
        type: "URL",
        text: template.showButton4_quick3,
        url: template.showButton4_quick3_url,
      });
    }

    if (
      includeButtons &&
      template.showButton4_quick4?.trim() &&
      template.showButton4_quick3_call?.trim()
    ) {
      buttons.push({
        type: "PHONE_NUMBER",
        text: template.showButton4_quick4,
        phone_number: template.showButton4_quick3_call,
      });
    }

    if (buttons.length > 0) {
      components.push({
        type: "BUTTONS",
        buttons,
      });
    }

    return {
      userid: userID,
      secret: secret || "string",
      name: template.name,
      language: template.language,
      category: template.category,
      purpose: "",
      phoneNumberId: selectedPhoneNumberId,
      document: documentUrl,

      components,
    };
  };

  const chooseAPI = async () => {
    try {
      if (!template.name || !template.language || !template.category) {
        toast.error("Please fill all required fields");
        return;
      }

      if (selectedNumberIndex === "" || !selectedPhoneNumberId) {
        toast.error("Please select WhatsApp number");
        return;
      }

      let response = null;

      // IMAGE
      if (template.headerType === "Image") {
        if (!headerImage) {
          toast.error("Please select image");
          return;
        }

        const base64 = await convertToBase64(headerImage);
        const uploadRes = await uploadImage(headerImage.name, base64);

        if (uploadRes?.status !== "0") {
          toast.error(uploadRes?.msg || "Image upload failed");
          return;
        }

        const payload = buildMediaPayload(uploadRes.image_url, "image");

        response = await createImageTemplate(payload);
      }

      // VIDEO
      else if (template.headerType === "Video") {
        if (!headerVideo) {
          toast.error("Please select video");
          return;
        }

        const uploadRes = await uploadVideo(headerVideo);
        if (uploadRes?.responseCode !== "00") {
          toast.error(uploadRes?.responseMessage || "Video upload failed");
          return;
        }

        const mediaUrl = uploadRes?.data?.mediaUrl;

        const payload = buildMediaPayload(mediaUrl, "video");

        response = await createVideoTemplate(payload);
      }
      // DOCUMENT
      else if (template.headerType === "Document") {
        if (!headerDocument) {
          toast.error("Please select document");
          return;
        }

        const uploadRes = await uploadDocument(headerDocument);

        if (uploadRes?.responseCode !== "00") {
          toast.error(uploadRes?.responseMessage || "Document upload failed");
          return;
        }

        const documentUrl = uploadRes?.data?.mediaUrl;

        if (!documentUrl) {
          toast.error("Document URL missing");
          return;
        }

        const payload = buildDocumentTemplatePayload(documentUrl);

        response = await createDocumentTemplate(payload);
      }

      // TEXT
      else {
        const payload = buildCreateTemplatePayload();

        response = await createTemplate(payload);
      }

      if (response?.responseCode === "00" && !response?.response?.error) {
        toast.success(
          response?.responseMessage || "Template Created Successfully",
        );
        await fetchVendorTemplates(selectedPhoneNumberId);
      } else {
        toast.error(
          response?.response?.error?.error_user_msg ||
            response?.response?.error?.message ||
            response?.responseMessage ||
            "Template creation failed",
        );
      }
    } catch (err) {
      console.error("Template Creation Error FULL:", err);
      toast.error(err?.message || "Something went wrong");
    }
  };

  const [template, setTemplate] = useState({
    name: "",
    language: "",
    category: "",
    headerType: "",
    headerText: "",
    body: "",
    footer: "Powered By Anlook",
    button1: "",
    button2: "",
  });

  const addVariable = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const nextVarNum = variables.length + 1;
    const variableText = ` {{${nextVarNum}}} `;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const newBody =
      template.body.substring(0, start) +
      variableText +
      template.body.substring(end);

    setTemplate((prev) => ({
      ...prev,
      body: newBody,
    }));

    setVariables((prev) => [...prev, { label: variableText, value: "" }]);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + variableText.length,
        start + variableText.length,
      );
    }, 0);
  };

  const handleVariableValueChange = (index, value) => {
    const updated = [...variables];
    updated[index].value = value;
    setVariables(updated);
  };

  const handleButtonTypeChange = (e) => {
    const { value } = e.target;

    if (value === "4") {
      setTemplate((prev) => ({
        ...prev,
        showButton4option_quick1: "4",
      }));
    }

    if (value === "5") {
      setTemplate((prev) => ({
        ...prev,
        showButton4option_quick2: "5",
      }));
    }

    if (value === "6") {
      setTemplate((prev) => ({
        ...prev,
        showButton4option_quick3: "6",
      }));
    }

    if (value === "7") {
      setTemplate((prev) => ({
        ...prev,
        showButton4option_quick4: "7",
      }));
    }
  };

  const applyFormatting = (type) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    if (start === end) return;

    const selectedText = template.body.substring(start, end);

    let formattedText = selectedText;

    if (type === "bold") {
      formattedText = `*${selectedText}*`;
    }

    if (type === "italic") {
      formattedText = `_${selectedText}_`;
    }

    if (type === "boldItalic") {
      formattedText = `_*${selectedText}*_`;
    }

    const newText =
      template.body.substring(0, start) +
      formattedText +
      template.body.substring(end);

    setTemplate((prev) => ({
      ...prev,
      body: newText,
    }));

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + formattedText.length);
    }, 0);
  };

  const insertEmoji = (emoji) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { selectionStart } = textarea;

    const newText =
      template.body.slice(0, selectionStart) +
      emoji +
      template.body.slice(selectionStart);

    setTemplate({ ...template, body: newText });

    setShowEmojiPicker(false);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        selectionStart + emoji.length,
        selectionStart + emoji.length,
      );
    }, 0);
  };

  const handleHeaderTypeChange = (value) => {
    setTemplate({
      ...template,
      headerType: value,
    });

    // reset media
    setHeaderImage(null);
    setHeaderVideo(null);
    setHeaderDocument(null);
  };

  const handleRemoveButton4_quick1 = () => {
    setTemplate((prev) => ({
      ...prev,
      showButton4_quick1: "",
      showButton4option_quick1: "",
    }));
  };

  const handleRemoveButton4_quick2 = () => {
    setTemplate((prev) => ({
      ...prev,
      showButton4_quick2: "",
      showButton4option_quick2: "",
    }));
  };

  const handleRemoveButton4_quick3 = () => {
    setTemplate((prev) => ({
      ...prev,
      showButton4_quick3: "",
      showButton4_quick3_url: "",
      showButton4option_quick3: "",
    }));
  };

  const handleRemoveButton4_quick4 = () => {
    setTemplate((prev) => ({
      ...prev,
      showButton4_quick4: "",
      showButton4_quick3_call: "",
      showButton4option_quick4: "",
    }));
  };

  const handlePhoneChange = (e) => {
    let inputValue = e.target.value.replace(/\D/g, "");

    if (!inputValue.startsWith(selectedCode)) {
      inputValue = selectedCode + inputValue;
    }

    setTemplate((prev) => ({
      ...prev,
      showButton4_quick3_call: inputValue,
    }));
  };

  const replaceVariables = (text = "") => {
    return variables.reduce((acc, variable, index) => {
      const pattern = new RegExp(`\\{\\{${index + 1}\\}\\}`, "g");
      return acc.replace(pattern, variable.value || "");
    }, text);
  };

  const renderFormattedText = (text = "") => {
    let formatted = text;

    // Escape HTML
    formatted = formatted
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    // Bold Italic first
    formatted = formatted.replace(/_\*(.*?)\*_/g, "<b><i>$1</i></b>");
    formatted = formatted.replace(/\*_(.*?)_\*/g, "<b><i>$1</i></b>");

    // Bold
    formatted = formatted.replace(/\*(.*?)\*/g, "<b>$1</b>");

    // Italic
    formatted = formatted.replace(/_(.*?)_/g, "<i>$1</i>");

    // Line breaks
    formatted = formatted.replace(/\n/g, "<br/>");

    return formatted;
  };

  /* ================= LOAD LANGUAGES ================= */
  useEffect(() => {
    const load = async () => {
      const res = await getLanguages();
      if (res?.responseCode === "00") {
        setLanguages(res.data);
      }
    };
    load();
  }, []);

  const selectedWhatsappNumber =
    selectedNumberIndex !== ""
      ? whatsappNumbers?.[Number(selectedNumberIndex)]
      : null;

  const selectedPhoneNumberId = selectedWhatsappNumber?.phone_no_id || "";

  useEffect(() => {
    if (whatsappNumbers?.length > 0 && selectedNumberIndex === "") {
      const firstValidIndex = whatsappNumbers.findIndex(
        (item) => item?.phone_no,
      );

      const defaultIndex = firstValidIndex !== -1 ? firstValidIndex : 0;

      setSelectedNumberIndex(String(defaultIndex));
    }
  }, [whatsappNumbers, selectedNumberIndex]);

  return (
    <>
      <NoIndex />
      <ToastContainer />
      <div className="h-full overflow-hidden p-8 montserrat-font-1">
  <div className="max-w-8xl mx-auto flex h-full gap-10 min-h-0 montserrat-font-1">
          
          {/* ================= LEFT FORM ================= */}
          <TemplateLeftForm
            selectedNumberIndex={selectedNumberIndex}
            setSelectedNumberIndex={setSelectedNumberIndex}
            whatsappNumbers={whatsappNumbers}
            template={template}
            setTemplate={setTemplate}
            languages={languages}
            includeHeader={includeHeader}
            setIncludeHeader={setIncludeHeader}
            handleHeaderTypeChange={handleHeaderTypeChange}
            headerImage={headerImage}
            setHeaderImage={setHeaderImage}
            headerVideo={headerVideo}
            setHeaderVideo={setHeaderVideo}
            headerDocument={headerDocument}
            setHeaderDocument={setHeaderDocument}
            textareaRef={textareaRef}
            applyFormatting={applyFormatting}
            addVariable={addVariable}
            variables={variables}
            handleVariableValueChange={handleVariableValueChange}
            showEmojiPicker={showEmojiPicker}
            setShowEmojiPicker={setShowEmojiPicker}
            emojiCategories={emojiCategories}
            selectedEmojiCategory={selectedEmojiCategory}
            setSelectedEmojiCategory={setSelectedEmojiCategory}
            insertEmoji={insertEmoji}
            includeButtons={includeButtons}
            setIncludeButtons={setIncludeButtons}
            handleButtonTypeChange={handleButtonTypeChange}
            handleRemoveButton4_quick1={handleRemoveButton4_quick1}
            handleRemoveButton4_quick2={handleRemoveButton4_quick2}
            handleRemoveButton4_quick3={handleRemoveButton4_quick3}
            handleRemoveButton4_quick4={handleRemoveButton4_quick4}
            selectedCode={selectedCode}
            setSelectedCode={setSelectedCode}
            countryCodes={countryCodes}
            handlePhoneChange={handlePhoneChange}
            chooseAPI={chooseAPI}
          />

          {/* ================= RIGHT MOBILE PREVIEW ================= */}
          <TemplateRightForm
            includeHeader={includeHeader}
            template={template}
            headerImage={headerImage}
            headerVideo={headerVideo}
            headerDocument={headerDocument}
            includeButtons={includeButtons}
            getDocumentExtension={getDocumentExtension}
            formatFileSize={formatFileSize}
            renderFormattedText={renderFormattedText}
            replaceVariables={replaceVariables}
          />
        </div>
      </div>
    </>
  );
};

export default CreateTemplate;
